/**
 * Client-side stone export — a signed-in verified buyer downloads THEIR OWN
 * selection of catalog stones, at the RAW prices they already see (no markup,
 * no share doc, no server round-trip). This is deliberately NOT the shares
 * export (`GET /api/shares/:id/xlsx`), which applies the broker's markup and
 * needs a share to exist first.
 *
 * We emit a CSV (Excel-openable), not a true .xlsx: there is no client-side
 * xlsx builder in the web bundle (the shares .xlsx is rendered server-side by
 * `exceljs` in functions/), and pulling a sheet lib into the browser bundle to
 * export a flat table the user is looking at is not worth the weight. The
 * escaping + download plumbing mirrors `ShareView.downloadCsv` so there is one
 * CSV shape across the app.
 *
 * Prices: `pricePerCarat` / `priceUSD` are the buyer-visible catalog numbers.
 * For an unverified caller they are null (server-locked) — those cells go
 * blank rather than fabricated.
 */
import type { Diamond } from '../api/types'
import { displayStockId } from './format'

const HEADERS = [
  '#',
  'Stock ID',
  'Shape',
  'Carat',
  'Color',
  'Clarity',
  'Cut',
  'Polish',
  'Symmetry',
  'Fluorescence',
  'Lab',
  'Cert No',
  'Measurements (mm)',
  '$/ct',
  'Total (USD)',
] as const

/** Quote a cell only when it contains a delimiter, quote, or newline. \r is
 *  included: a lone carriage return from a feed value would otherwise escape
 *  unquoted and split the row in Excel. */
function cell(v: unknown): string {
  const s = v == null ? '' : String(v)
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

function measStr(m: Diamond['measurements']): string {
  if (!m || m.length == null) return ''
  return `${m.length} × ${m.width} × ${m.height}`
}

/** Build the CSV text for a selection of catalog stones (no markup). */
export function stonesToCsv(stones: Diamond[]): string {
  const rows = stones.map((s, i) => [
    i + 1,
    displayStockId(s),
    s.shape ?? '',
    s.carat,
    s.color ?? '',
    s.clarity ?? '',
    s.cut ?? '',
    s.polish ?? '',
    s.symmetry ?? '',
    s.fluorescence ?? '',
    s.lab ?? '',
    s.certNo ?? '',
    measStr(s.measurements),
    // Blank (not "—", not 0) when the price is server-locked for this caller.
    s.pricePerCarat ?? '',
    s.priceUSD ?? '',
  ])
  return [HEADERS, ...rows].map((r) => r.map(cell).join(',')).join('\n')
}

/**
 * Export the given stones to a downloadable CSV file (`diaex-stones.csv`).
 * No-op on an empty selection.
 */
export function downloadStonesCsv(stones: Diamond[], filename = 'diaex-stones.csv'): void {
  if (stones.length === 0) return
  const csv = stonesToCsv(stones)
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
